import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../api/client';

export type AsyncState<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly data: T }
  | { readonly status: 'error'; readonly error: ApiError };

/**
 * Memuat data sekali dan menyediakan `reload` — pola yang sama di empat layar.
 *
 * Membatalkan penerapan hasil saat efek sudah tidak berlaku (`cancelled`), bukan membatalkan
 * request-nya. Tanpa itu, berpindah bulan dengan cepat membuat jawaban permintaan lama menimpa
 * yang baru, dan pengguna melihat data bulan yang bukan ia pilih.
 *
 * Galat apa pun dinormalisasi menjadi `ApiError` supaya `ErrorCard` selalu punya `code` dan
 * `status` untuk diterjemahkan — galat mentah akan tampil sebagai "terjadi kesalahan" tanpa
 * arah tindakan.
 */
export function useAsync<T>(
  load: () => Promise<T>,
  deps: readonly unknown[],
): {
  readonly state: AsyncState<T>;
  readonly reload: () => void;
} {
  const [state, setState] = useState<AsyncState<T>>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    load()
      .then((data) => {
        if (!cancelled) setState({ status: 'ready', data });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          error:
            error instanceof ApiError
              ? error
              : new ApiError(0, 'UNKNOWN', 'Kesalahan tidak dikenal.', null),
        });
      });
    return () => {
      cancelled = true;
    };
    // `load` sengaja TIDAK masuk daftar dependency: pemanggil menuliskannya sebagai closure baru
    // pada setiap render, sehingga menyertakannya akan memuat ulang tanpa henti. Yang menentukan
    // kapan memuat ulang adalah `deps` yang diberikan pemanggil — nilai, bukan identitas fungsi.
  }, [...deps, nonce]);

  return { state, reload };
}
